import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import FormModal from 'web-ui-kit/modals/FormModal';
import CartPopup from 'web-ui-kit/CartPopup';
import { usePrevious } from 'web-services/lib/hooks';
import { surfaceM, grayLightM } from 'web-ui-kit/colors';
import { MQ_FOR_TABLET_LANDSCAPE_UP } from 'web-ui-kit/media';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _templateObject, _templateObject2;

function AddedToCartPopup(_ref) {
  var _ref$productDetailsSt = _ref.productDetailsState,
      productDetailsState = _ref$productDetailsSt === void 0 ? {} : _ref$productDetailsSt,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$tieredPromoEnabl = _ref.tieredPromoEnabled,
      tieredPromoEnabled = _ref$tieredPromoEnabl === void 0 ? false : _ref$tieredPromoEnabl;
  var _productDetailsState$ = productDetailsState.addedToCart,
      addedToCart = _productDetailsState$ === void 0 ? null : _productDetailsState$;

  var _ref2 = addedToCart || {},
      _ref2$product = _ref2.product,
      product = _ref2$product === void 0 ? null : _ref2$product,
      _ref2$quantity = _ref2.quantity,
      quantity = _ref2$quantity === void 0 ? 1 : _ref2$quantity,
      _ref2$subtotal = _ref2.subtotal,
      subtotal = _ref2$subtotal === void 0 ? 0 : _ref2$subtotal;

  if (!product || !addedToCart) {
    return null;
  }

  var desktopWrapperRef = useRef(null);
  var prevProduct = usePrevious(product);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      timer = _useState2[0],
      setTimer = _useState2[1];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      shouldShow = _useState4[0],
      setShouldShow = _useState4[1]; // This kicks off a 3 second timer to hide the component.
  // After 3 seconds, the hide class will be added to
  // facilitate fade out animation.


  var resetTimer = function resetTimer() {
    var timer = setTimeout(function () {
      setShouldShow(false);
      dispatch({
        type: 'pd-cart-popup-hidden'
      });
    }, 3000);
    setTimer(timer);
  }; // Remove the fade out timer. This is used for cleanup of
  // event listener callbacks


  var clearTimer = function clearTimer() {
    clearTimeout(timer);
  }; // If we are rendering the popup for a different product,
  // we show the popup and kick off the 3 second timer.


  useEffect(function () {
    if (product && product !== prevProduct) {
      setShouldShow(true);
      resetTimer();
    }
  }, [product]); // This is responsible for mouse enter preventing the popup from
  // disappearing. Mouse leave should set a 3 second timer for fade
  // out.

  useEffect(function () {
    if (!desktopWrapperRef.current) {
      return function () {};
    }

    desktopWrapperRef.current.addEventListener('mouseenter', clearTimer);
    desktopWrapperRef.current.addEventListener('mouseleave', resetTimer);
    return function () {
      if (desktopWrapperRef.current) {
        desktopWrapperRef.current.removeEventListener('mouseenter', clearTimer);
        desktopWrapperRef.current.removeEventListener('mouseleave', resetTimer);
      }
    };
  }, [desktopWrapperRef.current, resetTimer, clearTimer]);

  var renderDesktopPortal = function renderDesktopPortal() {
    return (
      /*#__PURE__*/
      createPortal(
      /*#__PURE__*/
      React.createElement(PopupWrapper, {
        ref: desktopWrapperRef,
        className: shouldShow ? 'show' : ''
      },
      /*#__PURE__*/
      React.createElement(CartPopup, {
        dispatch: dispatch,
        products: [_objectSpread2(_objectSpread2({}, product), {}, {
          quantity: quantity
        })],
        subtotal: subtotal,
        tieredPromoEnabled: tieredPromoEnabled,
        variant: 'items-added'
      })), document.body)
    );
  };

  var PopupWrapper = styles.PopupWrapper,
      StyledFormModal = styles.StyledFormModal;
  return (
    /*#__PURE__*/
    React.createElement(React.Fragment, null, renderDesktopPortal(),
    /*#__PURE__*/
    React.createElement(StyledFormModal, {
      dispatch: dispatch,
      id: 'item-added'
    },
    /*#__PURE__*/
    React.createElement(CartPopup, {
      dispatch: dispatch,
      products: [_objectSpread2(_objectSpread2({}, product), {}, {
        quantity: quantity
      })],
      subtotal: subtotal,
      tieredPromoEnabled: tieredPromoEnabled,
      variant: 'items-added'
    })))
  );
}

var styles = {
  PopupWrapper: styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    position: fixed;\n    display: none;\n    background: ", ";\n    border: 1px solid ", ";\n    box-sizing: border-box;\n    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.25);\n    border-radius: 4px;\n    z-index: 10000;\n    right: 0.875rem;\n    top: 5rem;\n    overflow: hidden;\n    opacity: 0.0;\n    visibility: hidden;\n    transition: 0.45s all ease-in-out;\n\n    ", " {\n      display: block;\n    }\n\n    &.show {\n      opacity: 1;\n      visibility: visible;\n    }\n  "])), surfaceM, grayLightM, MQ_FOR_TABLET_LANDSCAPE_UP),
  StyledFormModal: styled(FormModal)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    .item-added-modal-container {\n      align-self: start;\n      margin: 0;\n      max-width: unset;\n    }\n\n    .item-added-modal {\n      border-radius: 0;\n      padding: 0;\n    }\n\n    ", " {\n      display: none;\n    }\n  "])), MQ_FOR_TABLET_LANDSCAPE_UP)
};
AddedToCartPopup.propTypes = {
  addingToCartState: PropTypes.object,
  dispatch: PropTypes.func
};
export default AddedToCartPopup;